import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import {API_INTG_URL} from "../../apis/api";

const api = axios.create({
    baseURL: API_INTG_URL,
    headers: {
      "Content-Type": "application/json",
    },
  });

// ✅ Axios interceptor to handle token expiration
api.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (error.response?.status === 401) {
        console.warn("⚠️ Access token expired. Attempting to refresh...");
        try {
          const refreshToken = localStorage.getItem("refresh_token");
          if (!refreshToken) throw new Error("No refresh token available");
          const response = await api.post("/api/auth/token/refresh/", {
            refresh: refreshToken,
          });
          const { access } = response.data;
          localStorage.setItem("access_token", access);
          setAuthToken(access);
          // Retry the failed request with the new token
          error.config.headers["Authorization"] = `Bearer ${access}`;
          return api.request(error.config);
        } catch (refreshError) {
          console.error("❌ Token refresh failed. Logging out...");
          localStorage.removeItem("access_token");
          localStorage.removeItem("refresh_token");
          window.location.href = "/"; // Redirect to login
          return Promise.reject(refreshError);
        }
      }
      return Promise.reject(error);
    }
  );

// ✅ Function to set the Authorization token
export const setAuthToken = (token) => {
    if (token) {
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common["Authorization"];
    }
  };

// Async thunk to fetch user profile
export const fetchProfile = createAsyncThunk(
    "profile/fetchProfile",
    async (_, { rejectWithValue }) => {
      try {
        const token = localStorage.getItem("access_token");
        if (!token) throw new Error("No authentication token found. Please log in.");
  
        setAuthToken(token); // Set the token globally for API requests
        const response = await api.get("/api/auth/profile/");
        return response.data; // Return the fetched profile data
      } catch (err) {
        return rejectWithValue(err.response?.data || err.message);
      }
    }
  );

// Async thunk to update user profile
export const updateProfile = createAsyncThunk(
    "profile/updateProfile",
    async (profileData, { rejectWithValue }) => {
      try {
        const token = localStorage.getItem("access_token");
        if (!token) throw new Error("No authentication token found. Please log in.");
  
        setAuthToken(token); // Set the token globally for API requests
  
        // Prepare FormData for file upload
        const formData = new FormData();
        for (const key in profileData) {
          if (profileData[key] instanceof File || profileData[key]) {
            formData.append(key, profileData[key]);
          }
        }
  
        const response = await api.put("/api/auth/profile/", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        return response.data; // Return the updated profile data
      } catch (err) {
        return rejectWithValue(err.response?.data || err.message);
      }
    }
  );

// Initial state
const initialState = {
    profileData: null,
    loading: false,
    error: null,
  };
  
  // Create slice
  const profileSlice = createSlice({
    name: "profile",
    initialState,
    reducers: {
      // Optional: Add a reducer to reset the profile state
      resetProfile: (state) => {
        state.profileData = null;
        state.loading = false;
        state.error = null;
      },
    },
    extraReducers: (builder) => {
      builder
        // Fetch profile cases
        .addCase(fetchProfile.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(fetchProfile.fulfilled, (state, action) => {
          state.loading = false;
          state.profileData = action.payload;
        })
        .addCase(fetchProfile.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload;
        })
        // Update profile cases
        .addCase(updateProfile.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(updateProfile.fulfilled, (state, action) => {
          state.loading = false;
          state.profileData = action.payload;
        })
        .addCase(updateProfile.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload;
        });
    },
  });
  
// Export actions
export const { resetProfile } = profileSlice.actions;

// Export reducer
export default profileSlice.reducer;
